// src/lib/shieldModel.js
// G360_K536b - credential-shaped test values stored reversed (secret scan).
//
// G360_K536 - one model for Data Shield and Policies: what each detector
// and each policy actually does, measured from the rows that drive
// enforcement. Pure functions; nothing here reads or writes the database.
//
//   Coverage   For every enabled detector (a data type) and every tool
//              status people can reach (sanctioned, under review, shadow),
//              the action that WINS - the same rule evaluatePolicies and
//              the extension use: every enabled policy that applies and is
//              triggered is considered, the most restrictive action wins,
//              and on a tie the first by priority keeps it. A policy scoped
//              to Entra groups covers only part of the population, so it
//              is shown as partial and never counted as full cover.
//   Required   Critical/High data types need redact or block; Medium/Low
//              need at least a warning.
//   Overlap    The firewall rule-base taxonomy (Al-Shaer & Hamed): a policy
//              that never decides because a stricter one always wins is
//              shadowed; one that always loses to an equal action is
//              redundant; overlap with a different action is a conflict.
//   Health     Detectors are tried against a synthetic test string for
//              their data type (a canary) and against a paragraph of
//              ordinary business text. Missing the canary or firing on the
//              ordinary text are shown as findings about the pattern, not
//              as proof of what happens on real traffic.
//
// Honest limits, printed on the pages: detection recall on real prompts is
// not measured; and traffic outside the enforcement planes is not seen.
//
// G360_K543 - the policy bundle carries each policy's mode (0282). Guard
// 0.1.4 and later record a simulation policy as sim_<action> and let the
// prompt through; older guards still enforce it. Simulated events are
// counted as "Simulated", never as blocks or redactions.

/* G360_K687 - WHAT THE BROWSER GUARD ACTUALLY APPLIES. The console lets a
   policy name any data type and any scope; the guard (content.bundle.js,
   decideByPolicies) applies only two scopes - everyone, and Entra groups
   where it knows the person's groups - and only its own built-in detectors.
   A policy can therefore read as healthy here and never fire in a browser.
   Each policy and detector now says so. Mirror of the guard's DETECTORS
   list; change both together. The three "too broad" types are left out of
   the guard on purpose: as written they match ordinary numbers and codes. */
export const GUARD_DETECTOR_KEYS = ['us_ssn', 'ein', 'uk_nino', 'aadhaar', 'credit_card', 'iban', 'aws_key', 'github_token', 'openai_key', 'slack_token',
  'google_api', 'private_key', 'jwt', 'generic_secret', 'email', 'phone_us', 'mrn', 'source_code',
  'inj_instruction_override', 'inj_role_reassignment', 'inj_system_prompt_probe', 'inj_guardrail_bypass', 'inj_delimiter_break', 'inj_exfil_instruction', 'inj_hidden_directive'];
const GUARD_SET = new Set(GUARD_DETECTOR_KEYS);
export const GUARD_TOO_BROAD = { npi: 'any 10-digit number', us_routing: 'any 9-digit number', us_ssn_nospace: 'any 9-digit number', icd10: 'a letter followed by two digits' };
export const GUARD_LEVEL = {
  full: { key: 'full', label: 'Applies', color: '#137333', bg: '#e6f4ea' },
  partial: { key: 'partial', label: 'Partly', color: '#b06000', bg: '#feefe3' },
  none: { key: 'none', label: 'Not applied', color: '#5f6368', bg: '#f1f3f4' },
  off: { key: 'off', label: 'Off', color: '#5f6368', bg: '#f1f3f4' },
};
export function guardChecks(key) { return GUARD_SET.has(key); }
/* G360_K875 - oldGuards: the caller has checked the fleet and found at least
   one device below 0.1.12. Defaulting to false means this function never
   asserts something about a fleet it cannot see; Endpoint Governance is where
   guard versions are reported, and it says so per device with its own date. */
export function guardReach(p, oldGuards = false) {
  if (!p || p.enabled === false) return { level: GUARD_LEVEL.off, notes: [], missing: [] };
  const st = p.scope_type || 'all';
  if (st !== 'all' && st !== 'entra_group' && st !== 'status') return { level: GUARD_LEVEL.none, missing: [], notes: ['Not applied by the browser guard: the guard applies only rules for everyone or for Entra groups, not rules scoped to ' + (st === 'teams' ? 'teams' : 'a single tool') + '.'] };
  const notes = []; let level = GUARD_LEVEL.full;
  /* G360_K819 - a tool-status scope now reaches the guard. It used to
     return none here, which was correct until K819: decideByPolicies
     skipped every status-scoped rule, so it recorded nothing and
     enforced nothing. Saying so is version-dependent, not absolute -
     the same shape as the simulation note, which says which guard
     records and which still enforces. */
  if (st === 'status') {
    /* G360_K871 - a status scope that names NO status matches nothing, and until
       now this function returned "Partly" for it: the same verdict, with the same
       notes, as a rule scoped correctly. scopeStatusesOf's own comment has
       forbidden that reading since K840 - "a status-scoped rule with no status
       matches NOTHING, and no caller may read that as 'all'" - and guardReach was
       the one caller that never asked it.

       ACE Technology Group has such a row live: scope_type 'status', scope_status
       null, scope_statuses null, action 'allow', enabled. A financial-account rule
       that has never been capable of firing, shown to its owner as partly applied.

       Returns early on purpose. The detector-reach notes below would say which of
       its data types the guard checks, which reads as "fix the detectors and it
       works" - it would not. Nothing about this rule works until it names a status.

       save() has refused to persist this since K840 and migration 0424 refuses an
       EMPTY array, but neither covers scope_statuses IS NULL, so a row written by
       the seeder, the API or SQL can still reach this state. 0428 closes that. */
    if (!scopeStatusesOf(p).length) {
      return {
        level: GUARD_LEVEL.none,
        missing: [],
        notes: ['Not applied by the browser guard: this rule is scoped to tool status but names no status, so it matches no tool at all. Open it and pick at least one status - leaving it empty is not a broader rule, it is an inert one.'],
      };
    }
    /* G360_K875 - a correctly scoped status rule is APPLIED, not "Partly".

       These two sentences used to be pushed here unconditionally, and because
       K687 lets a guard note set the band, every status-scoped rule on every
       tenant was permanently amber and could never read green. On ACE that is
       five of eight live policies - a wall of warnings describing how the
       feature works rather than anything wrong with the rules. The customer's
       words were that the page says things need attention with no way to
       resolve them. There was no way to resolve them because there was
       nothing to resolve.

       The first note asserted that guards older than 0.1.12 ignore a status
       scope. True, and not knowable from here: guardReach sees one policy and
       nothing about the fleet. Every reporting ACE device is on 0.1.12. It is
       now shown only when a caller passes oldGuards, having actually looked.

       The second described chatgpt.com serving both a consumer tool and a paid
       subscription. That is a property of host resolution, identical for every
       rule, and the AI Tool Access policy's own description already states it.
       It is not a limit on this rule's reach and does not belong on it. */
    if (oldGuards) {
      level = GUARD_LEVEL.partial;
      notes.push('Some devices report a guard older than 0.1.12, and those ignore a tool-status scope completely - on them this rule records nothing and enforces nothing. Update them from Endpoint Governance.');
    }
  }
  if (st === 'entra_group') {
    level = GUARD_LEVEL.partial;
    notes.push('Partly applied by the browser guard: only in browsers where a person signed in to the guard. A device that IT deployed the guard to has no person attached, so the guard does not apply group-scoped rules there.');
  }
  const keys = p.detector_keys || [];
  const missing = keys.filter((k) => !GUARD_SET.has(k));
  const broad = missing.filter((k) => GUARD_TOO_BROAD[k]);
  if (keys.length && missing.length === keys.length) {
    return { level: GUARD_LEVEL.none, missing, notes: notes.concat(['Not applied by the browser guard: it checks none of this rule\'s data types (' + missing.join(', ') + ').']
      .concat(broad.length ? [broad.join(', ') + ' ' + (broad.length === 1 ? 'is' : 'are') + ' left out on purpose: as written ' + (broad.length === 1 ? 'it matches ' : 'they match ') + broad.map((k) => GUARD_TOO_BROAD[k]).join('; ') + ', too often to enforce safely.'] : [])) };
  }
  if (missing.length) {
    level = GUARD_LEVEL.partial;
    notes.push('Partly applied by the browser guard: it does not check ' + missing.length + ' of this rule\'s ' + keys.length + ' data types (' + missing.join(', ') + ').');
    if (broad.length) notes.push(broad.join(', ') + ' ' + (broad.length === 1 ? 'is' : 'are') + ' left out on purpose: as written ' + (broad.length === 1 ? 'it matches ' : 'they match ') + broad.map((k) => GUARD_TOO_BROAD[k]).join('; ') + ', too often to enforce safely.');
  }
  /* G360_K875 - oldGuards travels with the verdict so a caller can turn it
     into a step with somewhere to go, instead of a dead end. */
  return { level, notes, missing, oldGuards: !!oldGuards };
}

export const RANK = { allow: 0, flag: 1, warn: 1, redact: 2, block: 3 };
export const ACT = {
  block: { label: 'Block', color: '#c5221f', bg: '#fce8e6' },
  redact: { label: 'Redact', color: '#c26401', bg: '#feefe3' },
  warn: { label: 'Warn', color: '#1a73e8', bg: '#e8f0fe' },
  flag: { label: 'Flag', color: '#7b1fa2', bg: '#f3e8fd' },
  allow: { label: 'Allow', color: '#137333', bg: '#e6f4ea' },
  none: { label: 'No policy', color: '#5f6368', bg: '#f1f3f4' },
  sim: { label: 'Simulated', color: '#00838f', bg: '#e0f7fa' },
};
export const actInfo = (a) => ACT[a] || { label: a ? String(a) : '-', color: '#5f6368', bg: '#f1f3f4' };
export const SEV_W = { Critical: 4, High: 3, Medium: 2, Low: 1 };
export const SEV_COLOR = { Critical: '#a50e0e', High: '#c5221f', Medium: '#b06000', Low: '#5f6368' };
export const sevW = (s) => SEV_W[s] || 2;
export const required = (sev) => (sev === 'Critical' || sev === 'High' ? 2 : 1);
export const STATUSES = [
  { key: 'sanctioned', label: 'Sanctioned tools', short: 'Sanctioned' },
  { key: 'review', label: 'Tools under review', short: 'Under review' },
  { key: 'shadow', label: 'Shadow AI', short: 'Shadow AI' },
];
export const BAND = {
  problem: { key: 'problem', label: 'Problem', color: '#c5221f', bg: '#fce8e6', rank: 0 },
  attention: { key: 'attention', label: 'Needs attention', color: '#c26401', bg: '#feefe3', rank: 1 },
  healthy: { key: 'healthy', label: 'Healthy', color: '#137333', bg: '#e6f4ea', rank: 2 },
  off: { key: 'off', label: 'Switched off', color: '#5f6368', bg: '#f1f3f4', rank: 3 },
};
export const SCORE_BANDS = [
  { min: 80, label: 'Strong', color: '#137333', bg: '#e6f4ea' },
  { min: 60, label: 'Fair', color: '#1a73e8', bg: '#e8f0fe' },
  { min: 40, label: 'Weak', color: '#c26401', bg: '#feefe3' },
  { min: 0, label: 'At risk', color: '#c5221f', bg: '#fce8e6' },
];
export const scoreBand = (v) => (v == null ? { label: 'Not measurable', color: '#5f6368', bg: '#f1f3f4' } : SCORE_BANDS.find((b) => v >= b.min));
export function daysSince(ts) { const t = ts ? Date.parse(ts) : NaN; return Number.isFinite(t) ? Math.floor((Date.now() - t) / 864e5) : null; }

/* ---------------------------- detector checks ---------------------------- */
/* Synthetic test strings (published test values, never real data).
   G360_K536b - the credential-shaped ones are stored REVERSED and turned
   round when the page runs. Written forwards they read as live secrets to
   Netlify's secret scanner, which failed the deploy on them (they are
   fakes - but a scanner cannot know that, and should not be told to
   ignore the pattern). A reversal is not folded away by the minifier, so
   neither the source nor the bundle carries the forward string. */
function rv(x) { return String(x).split('').reverse().join(''); }
export const CANARY = {
  us_ssn: '123-45-6789', us_ssn_nospace: '123456789', ssn: '123-45-6789',
  credit_card: '4111 1111 1111 1111', pan: '4111111111111111',
  email: 'jane.doe@example.com', phone_us: '(415) 555-0142', phone: '(415) 555-0142',
  aws_key: rv('ELPMAXE7NNDOFSOIAIKA'), aws_secret: rv('YEKELPMAXEYCifRxPb/GNEDM7K/IMEFntUXrlaJw'),
  github_token: rv('R8q7P6o5N4m3L2k1J0i9H8g7F6e5D4c3B2a1_phg'), private_key: rv('-----YEK ETAVIRP ASR NIGEB-----'),
  openai_key: rv('z4yXw1vUt8sRq5pOn2mLk9jIh6fEd3bA-jorp-ks'), generic_api_key: rv('"2l1k0j9i8h7g6f5e4d3c2b1a" = yek_ipa'),
  iban: 'GB82 WEST 1234 5698 7654 32', us_routing: '021000021', aadhaar: '2345 6789 0123',
  mrn: 'MRN: 12345678', npi: '1234567893', icd10: 'E11.9',
  source_code: 'function refund(user) { return api.charge(user, -amount); }',
  confidential: 'CONFIDENTIAL - do not distribute', ip_address: '192.168.10.24', ipv4: '192.168.10.24',
  jwt: rv('kXjEOFWFg1Ww_r1p1UJUbhu72K29BmPVC4ZeJtfjBd.0nI0MjMxIiOiIWdzJye.9JiN1IzUIJiOicGbhJye'),
  slack_token: rv('xWvUtSrQpOnMlKjIhGfEdCbA-3210987654321-210987654321-bxox'),
  stripe_key: rv('vutsrqponmlkjihgfedcba8H15_evil_ks'), uk_nino: 'QQ 12 34 56 C', passport_us: '123456789',
};
/* Ordinary business text that no sensitive-data detector should fire on. */
export const BENIGN = 'Order 482915736 shipped on 2026-09-14 and invoice INV-2026-0042 for $1,249.00 is attached. '
  + 'The build from commit 3f9a1c2e8b7d4a6f0e1c2b3a4d5e6f7a8b9c0d1e passed; version 10.0.19041 rolls out Tuesday. '
  + 'Tracking number 1Z999AA10123456784. Meet in room 4B at 10:30, or call the front desk on extension 4455. '
  + 'Please review the quarterly draft and the new onboarding checklist before Friday.';

function compile(d) {
  const f = String(d.flags || 'g');
  try { return { re: new RegExp(d.pattern || '', f.indexOf('g') >= 0 ? f : f + 'g') }; } catch (e) { return { err: e.message }; }
}
function count(re, text, out) {
  re.lastIndex = 0; let n = 0; let m; let guard = 0;
  while ((m = re.exec(text)) !== null && guard < 200) { n += 1; if (out && out.length < 3) out.push(m[0].slice(0, 48)); if (m.index === re.lastIndex) re.lastIndex += 1; guard += 1; }
  return n;
}
/* canary: true (matched), false (missed), null (no test string for this key
   or not a pattern detector). benign: matches in the ordinary text. */
export function checkDetector(d) {
  if (!d.pattern || (d.method && d.method !== 'regex')) return { compiles: null, canary: null, benign: null, canaryText: null };
  const c = compile(d);
  if (c.err) return { compiles: false, error: c.err, canary: null, benign: null, canaryText: null };
  const keys = [d.key].concat(Array.isArray(d.aliases) ? d.aliases : []);
  const k = keys.find((x) => CANARY[x]);
  const canaryText = k ? CANARY[k] : null;
  const benignSamples = [];
  const benign = count(c.re, BENIGN, benignSamples);
  return { compiles: true, canary: canaryText ? count(c.re, canaryText) > 0 : null, canaryText, benign, benignSamples };
}

/* ------------------------------- coverage ------------------------------- */
function canonMap(detectors) {
  const m = {};
  (detectors || []).forEach((d) => { m[d.key] = d.key; (d.aliases || []).forEach((a) => { if (a && !m[a]) m[a] = d.key; }); });
  return (k) => m[k] || k;
}
/* full | partial | null: does policy p apply to tools of this status? */
/* G360_K840 - the tool statuses a status-scoped policy applies to.
   scope_statuses is read FIRST and scope_status is the fallback, which is the
   exact order extension/content.bundle.js uses (G360_K819). Console coverage
   and guard enforcement therefore cannot disagree about which statuses a rule
   covers, and a guard bundle predating K819 still sees scope_status (which the
   console keeps set to the first element).
   Returns [] when neither is set: a status-scoped rule with no status matches
   NOTHING, and no caller may read that as "all". */
export function scopeStatusesOf(p) {
  if (!p) return [];
  if (Array.isArray(p.scope_statuses) && p.scope_statuses.length) return p.scope_statuses;
  return p.scope_status ? [p.scope_status] : [];
}

function appliesTo(p, status) {
  if (p.scope_type === 'all' || !p.scope_type) return p.scope_tool_id || p.scope_teams ? 'partial' : 'full';
  // G360_K840 - was `p.scope_status === status`, which under-reported coverage
  // for a rule naming several statuses: the console would show a gap the guard
  // does not have.
  if (p.scope_type === 'status') return scopeStatusesOf(p).indexOf(status) !== -1 ? 'full' : null;
  return 'partial'; // entra_group, a single tool, teams
}

export function buildShield({ detectors, policies, polHits, detHits, events, versions, bindings }) {
  const canon = canonMap(detectors);
  const dets = (detectors || []).slice();
  const on = dets.filter((d) => d.enabled);
  const pols = (policies || []).slice().sort((a, b) => ((a.priority == null ? 1e9 : a.priority) - (b.priority == null ? 1e9 : b.priority)) || String(a.created_at || '').localeCompare(String(b.created_at || '')));
  const live = pols.filter((p) => p.enabled !== false);
  const onKeys = new Set(on.map((d) => d.key));
  const allKeys = new Set(dets.map((d) => d.key));
  const triggers = (p, key) => { const ks = (p.detector_keys || []).map(canon); return ks.length ? ks.indexOf(key) >= 0 : true; };

  // cells[key][status] = { win, action, partial[], all[] }
  const cells = {};
  on.forEach((d) => {
    cells[d.key] = {};
    STATUSES.forEach((s) => {
      const c = { win: null, action: null, partial: [], all: [] };
      live.forEach((p) => {
        const ap = appliesTo(p, s.key);
        if (!ap || !triggers(p, d.key)) return;
        c.all.push({ p, ap });
        if (ap === 'partial') { c.partial.push(p); return; }
        if (!c.win || RANK[p.action] > RANK[c.win.action]) c.win = p;
      });
      c.action = c.win ? c.win.action : null;
      const need = required(d.severity);
      c.state = !c.win ? (c.partial.length ? 'partial' : 'none') : RANK[c.action] >= need ? 'ok' : 'weak';
      c.sim = !!(c.win && c.win.mode === 'simulation');
      cells[d.key][s.key] = c;
    });
  });

  // detector hit stats by key
  const DH = {}; (detHits || []).forEach((h) => { DH[h.detector_key] = h; });
  const PH = {}; (polHits || []).forEach((h) => { if (h.policy_id) PH[h.policy_id] = h; });
  const unattributed = (polHits || []).find((h) => !h.policy_id) || null;

  // versions per policy (newest first)
  const V = {}; (versions || []).forEach((v) => { (V[v.policy_id] = V[v.policy_id] || []).push(v); });
  Object.values(V).forEach((l) => l.sort((a, b) => (b.version || 0) - (a.version || 0)));
  const B = {}; (bindings || []).forEach((b) => { (B[b.policy_id] = B[b.policy_id] || []).push(b); });

  // events by policy / detector / tool
  const ev = events || [];
  const evByPolicy = {}; const evByDet = {};
  ev.forEach((e) => {
    if (e.policy_id) (evByPolicy[e.policy_id] = evByPolicy[e.policy_id] || []).push(e);
    (e.detector_keys || []).forEach((k) => { const ck = canon(k); (evByDet[ck] = evByDet[ck] || []).push(e); });
  });

  /* ---- policies ---- */
  const P = pols.map((p) => {
    const keys = (p.detector_keys || []).map(canon);
    const offKeys = keys.filter((k) => allKeys.has(k) && !onKeys.has(k));
    const missing = keys.filter((k) => !allKeys.has(k));
    const catchAll = keys.length === 0;
    const mine = []; // cells it fully applies to
    const partialCells = [];
    if (p.enabled !== false) {
      on.forEach((d) => STATUSES.forEach((s) => {
        const c = cells[d.key][s.key];
        const hit = c.all.find((x) => x.p === p);
        if (!hit) return;
        if (hit.ap === 'partial') partialCells.push({ d, s, c }); else mine.push({ d, s, c });
      }));
    }
    const wins = mine.filter((x) => x.c.win === p);
    const loseTo = {}; let stricter = 0; let equal = 0;
    mine.forEach((x) => {
      if (x.c.win === p) return;
      const w = x.c.win; loseTo[w.id] = w;
      if (RANK[w.action] > RANK[p.action]) stricter += 1; else equal += 1;
    });
    const conflicts = {};
    mine.forEach((x) => x.c.all.forEach((o) => { if (o.p !== p && o.ap === 'full' && o.p.action !== p.action) conflicts[o.p.id] = o.p; }));
    const shadowed = mine.length > 0 && wins.length === 0 && stricter > 0 && equal === 0;
    const redundant = mine.length > 0 && wins.length === 0 && !shadowed;
    const carried = wins.reduce((s, x) => s + sevW(x.d.severity) * RANK[p.action], 0);
    const h = PH[p.id] || null;
    const age = daysSince(p.created_at);
    const vs = V[p.id] || [];
    const lastChange = vs[0] ? vs[0].created_at : p.updated_at;
    const why = [];
    let noReach = false; // G360_K874
    const guard = guardReach(p); // G360_K687
    let band;
    if (p.enabled === false) { band = BAND.off; why.push('Switched off - nothing is enforced.'); }
    else {
      const problems = []; const attention = [];
      if (!catchAll && keys.length && offKeys.length === keys.length) problems.push('Every detector it listens for is switched off, so it can never fire.');
      /* G360_K874 - flagged, not just worded. The profile's What-to-do list
         had no step for this condition, so the headline reason on the one
         policy ACE had flagged red came with nothing to act on. A caller
         cannot re-derive it: `mine`, `partialCells` and `on` are local to
         this loop and never reach the policy object. */
      else if (p.scope_type !== 'entra_group' && mine.length === 0 && !partialCells.length && on.length) { noReach = true; problems.push('It applies to no data type on any tool status people can reach.'); }
      if (shadowed) problems.push('Never decides: a stricter policy wins everywhere it applies (' + Object.values(loseTo).map((x) => x.name).join(', ') + ').');
      if (redundant) attention.push('Redundant: another policy with the same action already decides everywhere it applies (' + Object.values(loseTo).map((x) => x.name).join(', ') + ').');
      if (offKeys.length && offKeys.length < keys.length) attention.push(offKeys.length + ' of its detectors are switched off (' + offKeys.join(', ') + ').');
      if (p.mode === 'simulation') attention.push('In simulation: guard 0.1.4 and later record what it would do (sim_' + (p.action || 'action') + ') and let the prompt through. Guards older than 0.1.4 still enforce it.');
      if (h && h.hits_window === 0 && (age == null || age > 30)) attention.push('No enforcement attributed to it in the last 30 days.');
      if (!p.description) attention.push('No description of what it is for.');
      if (Object.keys(conflicts).length) attention.push('Overlaps ' + Object.keys(conflicts).length + ' polic' + (Object.keys(conflicts).length === 1 ? 'y' : 'ies') + ' with a different action; the stricter one wins.');
      guard.notes.forEach((x) => attention.push(x)); // G360_K687 - never healthy while the guard does not apply it
      problems.forEach((x) => why.push(x)); attention.forEach((x) => why.push(x));
      band = problems.length ? BAND.problem : attention.filter((x) => x.indexOf('No description') !== 0 && x.indexOf('Overlaps') !== 0).length ? BAND.attention : BAND.healthy;
      if (band === BAND.healthy) why.unshift(wins.length ? 'Decides ' + wins.length + ' data-type and tool-status combination' + (wins.length === 1 ? '' : 's') + '.' : 'Applies as intended.');
    }
    return { p, guard, keys, offKeys, missing, catchAll, mine, partialCells, wins, loseTo: Object.values(loseTo), conflicts: Object.values(conflicts), shadowed, redundant, carried,
      hits: h, age, versions: vs, lastChange, bindings: B[p.id] || [], events: evByPolicy[p.id] || [], band, why, noReach };
  });
  const byCarried = P.filter((x) => x.p.enabled !== false).slice().sort((a, b) => (b.carried - a.carried) || ((b.hits ? b.hits.hits_window : 0) - (a.hits ? a.hits.hits_window : 0)));
  byCarried.forEach((x, i) => { x.rank = i + 1; x.of = byCarried.length; });
  const PById = {}; P.forEach((x) => { PById[x.p.id] = x; });

  /* ---- detectors ---- */
  const D = dets.map((d) => {
    const chk = checkDetector(d);
    const h = DH[d.key] || null;
    const listedBy = live.filter((p) => (p.detector_keys || []).map(canon).indexOf(d.key) >= 0);
    const caughtBy = live.filter((p) => !(p.detector_keys || []).length);
    const cov = d.enabled ? cells[d.key] : null;
    const states = cov ? STATUSES.map((s) => cov[s.key].state) : [];
    const why = [];
    let band;
    /* G360_K807 - the guard note is now carried on the row, not only
       folded into `why`. DetectorProfile's "What to do" never saw it,
       so a detector the guard cannot check listed other work and
       silently dropped the one fact that decides whether anything
       happens in a browser. `guard` stays a boolean - nothing that
       reads it changes. */
    let guardNote = null;
    if (!d.enabled) { band = BAND.off; why.push('Switched off - this data type is not examined.'); }
    else {
      const problems = []; const attention = [];
      if (chk.compiles === false) problems.push('The pattern does not compile (' + chk.error + '), so it never matches.');
      if (chk.canary === false) problems.push('It did not match the standard test value for its type (' + chk.canaryText + ').');
      if (states.length && states.every((x) => x === 'none' || x === 'partial')) problems.push('No policy acts on it for any tool status' + (listedBy.length ? '' : ' - no policy lists it') + '.');
      if (cov && (d.severity === 'Critical' || d.severity === 'High') && cov.shadow.state !== 'ok' && !states.every((x) => x === 'none' || x === 'partial')) attention.push('A ' + d.severity + ' type that is not redacted or blocked on shadow AI.');
      const gapSt = cov ? STATUSES.filter((s) => cov[s.key].state === 'none') : [];
      if (gapSt.length && gapSt.length < STATUSES.length && !states.every((x) => x === 'none' || x === 'partial')) attention.push('No policy acts on it on ' + gapSt.map((s) => s.label.toLowerCase()).join(' or ') + '.');
      else if (cov && STATUSES.some((s) => cov[s.key].state === 'weak')) attention.push('Acted on more weakly than its ' + (d.severity || 'Medium') + ' severity needs on ' + STATUSES.filter((s) => cov[s.key].state === 'weak').map((s) => s.label.toLowerCase()).join(' or ') + '.');
      if (chk.benign) attention.push('Matched ' + chk.benign + ' time' + (chk.benign === 1 ? '' : 's') + ' in ordinary business text - expect false positives.');
      if (h && h.hits_window === 0 && chk.canary !== true) attention.push('No hit in 30 days and no test value to prove it works.');
      // G360_K687 - the browser guard runs its own built-in detectors only.
      if (!GUARD_SET.has(d.key)) {
        guardNote = GUARD_TOO_BROAD[d.key]
          ? 'Not checked by the browser guard, on purpose: as written it matches ' + GUARD_TOO_BROAD[d.key] + ', too often to enforce safely.'
          : 'Not checked by the browser guard: no policy acts on this data type in the browser, whatever the policies list.';
        attention.push(guardNote);
      }
      problems.forEach((x) => why.push(x)); attention.forEach((x) => why.push(x));
      band = problems.length ? BAND.problem : attention.length ? BAND.attention : BAND.healthy;
      if (band === BAND.healthy) why.unshift(chk.canary ? 'Matches its test value, stays quiet on ordinary text, and a policy acts on it.' : 'A policy acts on it at the strength its severity needs.');
    }
    return { d, chk, guard: GUARD_SET.has(d.key), guardNote, hits: h, listedBy, caughtBy, cov, states, events: evByDet[d.key] || [], band, why };
  });
  const byHits = D.filter((x) => x.d.enabled).slice().sort((a, b) => ((b.hits ? b.hits.hits_window : 0) - (a.hits ? a.hits.hits_window : 0)) || (sevW(b.d.severity) - sevW(a.d.severity)));
  byHits.forEach((x, i) => { x.rank = i + 1; x.of = byHits.length; });
  const DByKey = {}; D.forEach((x) => { DByKey[x.d.key] = x; });

  return { cells, P, PById, D, DByKey, on, live, pols, dets, canon, unattributed, events: ev };
}

/* ------------------------------ the score ------------------------------ */
/* Policy coverage and effectiveness, 0-100, with its drivers. A driver that
   cannot be measured is left out and its weight spread over the others. */
export function shieldScore(m, { eventsErr, hitsErr } = {}) {
  const drivers = [];
  // A. risk-weighted coverage
  let num = 0; let den = 0;
  m.on.forEach((d) => STATUSES.forEach((s) => {
    const c = m.cells[d.key][s.key]; const w = sevW(d.severity); den += w;
    if (c.win) num += w * Math.min(1, RANK[c.action] / required(d.severity));
  }));
  drivers.push({ key: 'coverage', label: 'Risk-weighted coverage', weight: 45, value: den ? Math.round((num / den) * 100) : null,
    what: 'each enabled data type on sanctioned, under-review and shadow tools, weighted by severity, credited by the winning action against what its severity needs' });
  // B. integrity
  const liveP = m.P.filter((x) => x.p.enabled !== false);
  const clean = liveP.filter((x) => x.band.key !== 'problem' && !x.redundant && x.p.mode !== 'simulation').length;
  drivers.push({ key: 'integrity', label: 'Policy integrity', weight: 20, value: liveP.length ? Math.round((clean / liveP.length) * 100) : null,
    what: 'enabled policies that can fire, decide something, are not duplicated and are not in simulation' });
  // C. attribution
  const recent = (m.events || []).filter((e) => { const d = daysSince(e.created_at); return d != null && d <= 30; });
  const attributed = recent.filter((e) => e.policy_id).length;
  drivers.push({ key: 'attribution', label: 'Enforcement attribution', weight: 15, value: eventsErr || !recent.length ? null : Math.round((attributed / recent.length) * 100),
    what: 'enforcement events in the last 30 days that a policy is responsible for', unmeasured: eventsErr ? 'events could not be read' : !recent.length ? 'no enforcement events in 30 days' : null });
  // D. detector health
  const testable = m.D.filter((x) => x.d.enabled && x.chk.compiles !== null);
  const good = testable.filter((x) => x.chk.compiles && x.chk.canary !== false && !x.chk.benign).length;
  drivers.push({ key: 'detectors', label: 'Detector health', weight: 10, value: testable.length ? Math.round((good / testable.length) * 100) : null,
    what: 'enabled pattern detectors that compile, match their test value where one exists, and stay quiet on ordinary text' });
  // E. hygiene
  const hyg = liveP.filter((x) => x.p.description && (!x.hits || x.hits.hits_window > 0 || (x.age != null && x.age <= 30))).length;
  drivers.push({ key: 'hygiene', label: 'Hygiene', weight: 10, value: hitsErr || !liveP.length ? null : Math.round((hyg / liveP.length) * 100),
    what: 'enabled policies with a stated purpose that have fired in the last 30 days (or are newer than that)', unmeasured: hitsErr ? 'hit counts could not be read' : null });
  const meas = drivers.filter((d) => d.value != null);
  const wsum = meas.reduce((s, d) => s + d.weight, 0);
  let score = wsum ? Math.round(meas.reduce((s, d) => s + d.value * d.weight, 0) / wsum) : null;
  // Hard cap: a Critical data type reaching shadow AI with no redact/block.
  const critGaps = m.on.filter((d) => d.severity === 'Critical' && m.cells[d.key].shadow.state !== 'ok');
  const capped = score != null && critGaps.length > 0 && score > 39;
  if (capped) score = 39;
  drivers.forEach((d) => { d.points = d.value == null || !wsum ? null : Math.round((d.value * d.weight) / wsum); });
  return { score, drivers, critGaps, capped, measuredWeight: wsum };
}

/* --------------------------- time and heat ---------------------------- */
export function actionsByDay(events, days) {
  const n = days || 30; const out = [];
  const start = new Date(); start.setUTCHours(0, 0, 0, 0);
  for (let i = n - 1; i >= 0; i -= 1) { const t = new Date(start.getTime() - i * 864e5); out.push({ day: t.toISOString().slice(0, 10), block: 0, redact: 0, warn: 0, flag: 0, allow: 0, sim: 0, other: 0, inbound: 0, total: 0 }); }
  const idx = {}; out.forEach((b, i) => { idx[b.day] = i; });
  (events || []).forEach((e) => {
    const k = String(e.created_at || '').slice(0, 10); const i = idx[k]; if (i == null) return;
    // G360_K543 - a simulated action did not happen; it is its own bucket.
    const raw = String(e.action || '').toLowerCase();
    const a = raw.indexOf('sim_') === 0 ? 'sim' : raw;
    if (out[i][a] != null && a !== 'total' && a !== 'inbound') out[i][a] += 1; else out[i].other += 1;
    if (e.direction === 'inbound') out[i].inbound += 1;
    out[i].total += 1;
  });
  return out;
}
export function detectorToolHeat(events, canon, maxT, maxK) {
  const cell = {}; const T = {}; const K = {};
  (events || []).forEach((e) => {
    const t = e.tool_name || 'Unknown tool';
    (e.detector_keys || []).forEach((k0) => {
      const k = canon ? canon(k0) : k0; const key = t + '|' + k;
      const c = cell[key] || (cell[key] = { n: 0, block: 0, unattributed: 0 });
      c.n += 1; if (String(e.action) === 'block') c.block += 1; if (!e.policy_id) c.unattributed += 1;
      T[t] = (T[t] || 0) + 1; K[k] = (K[k] || 0) + 1;
    });
  });
  const tools = Object.entries(T).sort((a, b) => b[1] - a[1]).slice(0, maxT || 8).map((x) => x[0]);
  const keys = Object.entries(K).sort((a, b) => b[1] - a[1]).slice(0, maxK || 8).map((x) => x[0]);
  return { tools, keys, cell, max: Math.max(1, ...Object.values(cell).map((c) => c.n)) };
}

/* ------------------------------ linkage ------------------------------- */
/* Data Shield: data type -> policy -> where it applies. Red where a
   Critical/High data type has no policy acting on it. */
export function buildShieldLinks(m) {
  const L = []; const M = {}; const R = {}; const lines = [];
  const SCOPE = { all: 'All AI tools', sanctioned: 'Sanctioned tools', review: 'Tools under review', shadow: 'Shadow AI', blocked: 'Blocked tools', entra_group: 'Entra groups', other: 'A single tool or team' };
  /* G360_K840 - KNOWN LIMITATION, recorded rather than hidden: this groups the
     diagram by a policy's FIRST status, so a rule covering shadow and review is
     drawn under shadow only. The reach rows below list every status, and
     appliesTo() scores all of them, so no coverage number is wrong - only this
     one diagram's grouping is incomplete. */
  const scopeKey = (p) => (p.scope_type === 'status' ? (scopeStatusesOf(p)[0] || 'other') : p.scope_type === 'entra_group' ? 'entra_group' : (p.scope_tool_id || p.scope_teams) ? 'other' : 'all');
  m.D.filter((x) => x.d.enabled).forEach((x) => {
    const d = x.d; const hi = d.severity === 'Critical' || d.severity === 'High';
    const none = x.states.every((s) => s === 'none' || s === 'partial');
    L.push({ key: 'd:' + d.key, label: d.name || d.key, sub: [d.severity, d.category].filter(Boolean).join(' \u00b7 '), hot: none && hi, warn: x.band.key !== 'healthy', det: x });
    const ps = x.listedBy.concat(x.caughtBy);
    if (!ps.length || none) {
      const nk = 'p:none';
      M[nk] = M[nk] || { key: nk, label: 'No policy acts on it', sub: '', n: 0, hot: true };
      M[nk].n += 1;
      lines.push({ key: 'l:' + d.key + ':none', from: 'd:' + d.key, to: nk, n: 1, crit: hi ? 1 : 0 });
    }
    ps.forEach((p) => {
      const pk = 'p:' + p.id; const px = m.PById[p.id];
      M[pk] = M[pk] || { key: pk, label: p.name, n: 0, dashed: p.mode === 'simulation', warn: px && px.band.key !== 'healthy', hot: px && px.band.key === 'problem', pol: px };
      M[pk].n += 1;
      lines.push({ key: 'l:' + d.key + ':' + p.id, from: 'd:' + d.key, to: pk, n: 1, crit: 0 });
    });
  });
  Object.values(M).forEach((n) => {
    if (n.key === 'p:none') { n.sub = n.n + ' data type' + (n.n === 1 ? '' : 's'); return; }
    const p = n.pol.p; const sk = scopeKey(p);
    n.sub = actInfo(p.action).label + ' \u00b7 ' + n.n + ' type' + (n.n === 1 ? '' : 's') + (p.mode === 'simulation' ? ' \u00b7 simulation' : '');
    const rk = 's:' + sk;
    R[rk] = R[rk] || { key: rk, label: SCOPE[sk], n: 0, crit: 0, dashed: sk === 'entra_group' || sk === 'other' };
    R[rk].n += 1;
    lines.push({ key: 'l2:' + p.id, from: n.key, to: rk, n: n.n, crit: 0 });
  });
  Object.values(R).forEach((n) => { n.sub = n.n + ' polic' + (n.n === 1 ? 'y' : 'ies') + (n.dashed ? ' \u00b7 part of the population' : ''); });
  const sevO = (x) => -(sevW((x.det && x.det.d.severity) || 'Medium'));
  return {
    left: L.sort((a, b) => ((b.hot ? 1 : 0) - (a.hot ? 1 : 0)) || (sevO(a) - sevO(b))),
    mid: Object.values(M).sort((a, b) => ((b.hot ? 1 : 0) - (a.hot ? 1 : 0)) || (b.n - a.n)),
    right: Object.values(R).sort((a, b) => b.n - a.n),
    lines,
  };
}

/* G360_K558 - Policies: what each policy is CONFIGURED to protect and for
   whom - data type -> policy -> who and where it applies. Built from the
   policies, detectors and Entra group bindings, so it is drawn even before
   any enforcement event exists. Red: a High or Critical data type no policy
   acts on, or a policy that does nothing as written. Dashed: simulation, an
   exception group, or a scope that is only part of the population. */
export function buildPolicyReachLinks(m) {
  const L = []; const M = {}; const R = {}; const lines = {};
  const line = (from, to, n, crit) => { const k = from + '>' + to; const l = lines[k] || (lines[k] = { key: k, from, to, n: 0, crit: 0 }); l.n += n || 1; if (crit) l.crit += n || 1; };
  const STATUS_LABEL = { sanctioned: 'Sanctioned AI tools', review: 'Tools under review', shadow: 'Shadow AI tools', blocked: 'Blocked tools' };
  (m.D || []).filter((x) => x.d.enabled).forEach((x) => {
    const d = x.d; const hi = d.severity === 'Critical' || d.severity === 'High';
    const ps = (x.listedBy || []).concat(x.caughtBy || []).filter((p, i, a) => a.findIndex((q) => q.id === p.id) === i && p.enabled !== false);
    const none = !ps.length || (x.states || []).every((st) => st === 'none' || st === 'partial');
    L.push({ key: 'd:' + d.key, label: d.name || d.key, sub: [d.severity, d.category].filter(Boolean).join(' \u00b7 ') + (none ? ' \u00b7 no policy acts' : ''), hot: none && hi, warn: none && !hi, det: x });
    if (!ps.length) {
      const nk = 'p:none';
      M[nk] = M[nk] || { key: nk, label: 'No policy acts on it', n: 0, hot: true, dashed: true };
      M[nk].n += 1;
      line('d:' + d.key, nk, 1, hi);
    }
    ps.forEach((p) => {
      const pk = 'p:' + p.id; const px = m.PById[p.id];
      M[pk] = M[pk] || { key: pk, label: p.name, n: 0, dashed: p.mode === 'simulation', hot: !!(px && px.band.key === 'problem'), warn: !!(px && px.band.key !== 'healthy' && px.band.key !== 'problem'), pol: px };
      M[pk].n += 1;
      line('d:' + d.key, pk, 1, !!(px && px.band.key === 'problem'));
    });
  });
  Object.values(M).forEach((n) => {
    if (!n.pol) { n.sub = n.n + ' data type' + (n.n === 1 ? '' : 's'); return; }
    const p = n.pol.p; const px = n.pol;
    const hits = px.hits ? px.hits.hits_window : null;
    n.sub = actInfo(p.action).label + ' \u00b7 ' + n.n + ' type' + (n.n === 1 ? '' : 's') + (hits != null ? ' \u00b7 ' + hits + ' hits/30d' : '') + (p.mode === 'simulation' ? ' \u00b7 simulation' : '');
    const crit = px.band.key === 'problem';
    const addR = (rk, label, dashed, people) => {
      const r = R[rk] || (R[rk] = { key: rk, label, n: 0, dashed: !!dashed, people: people == null ? null : 0 });
      r.n += 1; if (people != null) r.people = (r.people || 0) + Number(people || 0);
      line(n.key, rk, 1, crit);
    };
    if (p.scope_type === 'entra_group') {
      const bs = px.bindings || [];
      const scope = bs.filter((b) => b.kind !== 'exception');
      if (!scope.length) addR('s:nogroup', 'Entra groups - none bound', true, null);
      scope.forEach((b) => addR('g:' + b.entra_group_id, (b.display_name || b.entra_group_id), false, b.member_count));
      bs.filter((b) => b.kind === 'exception').forEach((b) => addR('x:' + b.entra_group_id, 'Except ' + (b.display_name || b.entra_group_id), true, b.member_count));
    } else if (p.scope_type === 'status' && scopeStatusesOf(p).length) {
      /* G360_K840 - one row per status. A rule covering shadow AND review
         reaches both, and showing only the first would understate its reach. */
      scopeStatusesOf(p).forEach((s) => addR('s:' + s, STATUS_LABEL[s] || s, false, null));
    } else if (p.scope_tool_id || p.scope_teams) {
      addR('s:one', 'A single tool or team', true, null);
    } else {
      addR('s:all', 'Everyone, every AI tool', false, null);
    }
  });
  Object.values(R).forEach((r) => {
    r.sub = r.n + ' polic' + (r.n === 1 ? 'y' : 'ies') + (r.people ? ' \u00b7 ' + r.people + ' people' : '') + (r.key === 's:nogroup' ? ' \u00b7 applies to nobody' : '');
    if (r.key === 's:nogroup') r.hot = true;
  });
  const sevO = (x) => -(sevW((x.det && x.det.d.severity) || 'Medium'));
  return {
    left: L.sort((a, b) => ((b.hot ? 1 : 0) - (a.hot ? 1 : 0)) || (sevO(a) - sevO(b))),
    mid: Object.values(M).sort((a, b) => ((b.hot ? 1 : 0) - (a.hot ? 1 : 0)) || (b.n - a.n)),
    right: Object.values(R).sort((a, b) => ((b.hot ? 1 : 0) - (a.hot ? 1 : 0)) || (b.n - a.n)),
    lines: Object.values(lines),
  };
}

/* Policies: what enforcement actually did - policy -> AI tool -> action,
   from the events read. Red where no policy is attributed. */
/* G360_K558 - `by` picks the right-hand column: 'outcome' (what the policy
   did - the original map), 'user' (who it acted on; a device label with no
   email is grouped as "No user identity"), or 'data' (the data types that
   fired). Prompt events record the AI tool, not the model or an agent. */
export const EVIDENCE_BY = [
  { key: 'outcome', label: 'Outcome' },
  { key: 'user', label: 'User' },
  { key: 'data', label: 'Data type' },
];
export function buildEvidenceLinks(m, by) {
  const mode = by || 'outcome';
  const L = {}; const M = {}; const R = {}; const lines = {};
  const line = (from, to, crit) => { const k = from + '>' + to; const l = lines[k] || (lines[k] = { key: k, from, to, n: 0, crit: 0 }); l.n += 1; if (crit) l.crit += 1; };
  (m.events || []).forEach((e) => {
    const un = !e.policy_id;
    const pk = un ? 'p:none' : 'p:' + e.policy_id;
    const px = un ? null : m.PById[e.policy_id];
    const pl = L[pk] || (L[pk] = { key: pk, label: un ? 'No policy attributed' : (px ? px.p.name : (e.policy_name || 'Deleted policy')), n: 0, crit: 0, dashed: un, pol: px });
    pl.n += 1; if (un) pl.crit += 1;
    const tk = 't:' + (e.tool_name || 'Unknown tool');
    const tl = M[tk] || (M[tk] = { key: tk, label: e.tool_name || 'Unknown tool', n: 0, crit: 0 });
    tl.n += 1; if (un) tl.crit += 1;
    line(pk, tk, un);
    if (mode === 'user') {
      const raw = String(e.user_email || '').trim();
      const person = raw.indexOf('@') > 0;
      const uk = person ? 'u:' + raw.toLowerCase() : 'u:(none)';
      const ul = R[uk] || (R[uk] = { key: uk, label: person ? raw : 'No user identity', n: 0, crit: 0, dashed: !person, user: person ? raw : null, devices: {} });
      if (!person && raw) ul.devices[raw] = true;
      ul.n += 1; if (un) ul.crit += 1;
      line(tk, uk, un);
      return;
    }
    if (mode === 'data') {
      const keys = (Array.isArray(e.detector_keys) && e.detector_keys.length) ? e.detector_keys : ['(none)'];
      keys.forEach((k0) => {
        const k = m.canon ? m.canon(k0) : k0;
        const dx = m.DByKey ? m.DByKey[k] : null;
        const dk = 'd:' + k;
        const dl = R[dk] || (R[dk] = { key: dk, label: k === '(none)' ? 'No data type recorded' : (dx ? (dx.d.name || k) : k), n: 0, crit: 0, dashed: k === '(none)', det: dx });
        dl.n += 1; if (un) dl.crit += 1;
        line(tk, dk, un);
      });
      return;
    }
    const raw = String(e.action || 'other').toLowerCase();
    const isSim = raw.indexOf('sim_') === 0; const a = isSim ? raw.slice(4) : raw;
    const ak = 'a:' + (isSim ? 'sim_' : '') + a + ':' + (e.direction === 'inbound' ? 'in' : 'out');
    const al = R[ak] || (R[ak] = { key: ak, label: (isSim ? 'Simulated ' + actInfo(a).label.toLowerCase() : actInfo(a).label) + (e.direction === 'inbound' ? ' (response)' : ' (prompt)'), n: 0, crit: 0, dashed: isSim });
    al.n += 1; if (un) al.crit += 1;
    line(tk, ak, un);
  });
  const fin = (o) => Object.values(o).map((n) => ({ ...n, hot: n.crit > 0, sub: n.n + ' event' + (n.n === 1 ? '' : 's') + (n.crit ? ' \u00b7 ' + n.crit + ' unattributed' : '') })).sort((a, b) => b.n - a.n);
  return { left: fin(L), mid: fin(M), right: fin(R), lines: Object.values(lines) };
}

/* A version's changes against the one before it. */
const DIFF_FIELDS = [['name', 'Name'], ['action', 'Action'], ['enabled', 'On'], ['mode', 'Mode'], ['scope_type', 'Scope'], ['scope_status', 'Status scope'], ['scope_statuses', 'Statuses'], ['priority', 'Priority'], ['detector_keys', 'Detectors'], ['description', 'Description']];
export function versionDiff(v, prev) {
  const a = (prev && prev.snapshot) || null; const b = v.snapshot || {};
  if (!a) return ['Created'];
  const out = [];
  DIFF_FIELDS.forEach(([k, label]) => {
    const x = JSON.stringify(a[k] == null ? null : a[k]); const y = JSON.stringify(b[k] == null ? null : b[k]);
    if (x !== y) out.push(label + ': ' + (k === 'detector_keys' ? ((a[k] || []).length + ' -> ' + (b[k] || []).length) : (a[k] == null ? '-' : String(a[k])) + ' -> ' + (b[k] == null ? '-' : String(b[k]))));
  });
  return out.length ? out : ['Saved with no change to these fields'];
}
